import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { Mail } from 'lucide-react'
import { authApi } from '../api/auth'

interface FormData { email: string }

export function ForgotPasswordPage() {
  useEffect(() => { document.title = 'Forgot Password' }, [])
  const [submitted, setSubmitted] = useState(false)
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>()

  const mutation = useMutation({
    mutationFn: (dto: FormData) => authApi.forgotPassword(dto.email),
    // Always show the same panel after submit, even on transient backend
    // failure — surfacing different UI for success vs. error would itself
    // leak whether the email exists / the system is up.
    onSettled: () => setSubmitted(true),
  })

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-600 mb-4">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Forgot password?</h1>
          <p className="text-sm text-gray-500 mt-1">
            We'll email you a link to reset it.
          </p>
        </div>

        {submitted ? (
          <div className="card p-6 space-y-4 text-center">
            <p className="text-sm text-gray-700">
              If an account exists for that email, we've sent a reset link.
              Check your inbox (and spam folder).
            </p>
            <p className="text-xs text-gray-500">The link expires in 30 minutes.</p>
            <Link to="/login" className="btn btn-primary w-full">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(d => mutation.mutate(d))}
            className="card p-6 space-y-4"
          >
            <div>
              <label className="label">Email</label>
              <input
                {...register('email', { required: 'Email is required' })}
                type="email"
                autoComplete="email"
                className="input"
                placeholder="you@company.com"
              />
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>}
            </div>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn-primary btn w-full"
            >
              {mutation.isPending ? 'Sending...' : 'Send reset link'}
            </button>
            <div className="text-center text-sm">
              <Link to="/login" className="text-primary-600 hover:underline">
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
